/**
 * Instance Repository V2 - Factory Function Pattern
 *
 * FERP Phase 3 EXECUTE primitive for runtime workflow state.
 * Uses modular GraphQL queries/mutations for maintainability.
 *
 * @package workflow-forms
 */

import { ApolloClient } from '@apollo/client';
import {
  GqlInstance,
  GqlCreateInstanceInput,
  GqlUpdateInstanceInput,
  InstanceStatus,
} from './types';
import {
  GET_INSTANCE,
  LIST_INSTANCES_BY_STATUS,
  LIST_INSTANCES_BY_WORKFLOW,
  GET_INSTANCE_BY_CORRELATION,
  LIST_ACTIVE_INSTANCES,
} from './queries';
import {
  CREATE_INSTANCE,
  START_INSTANCE,
  PAUSE_INSTANCE,
  RESUME_INSTANCE,
  COMPLETE_INSTANCE,
  FAIL_INSTANCE,
  CANCEL_INSTANCE,
  UPDATE_INSTANCE_PROGRESS,
  UPDATE_INSTANCE,
  RECORD_INSTANCE_EVENT,
} from './mutations';

// =============================================================================
// Repository Interface
// =============================================================================

/**
 * Instance Repository V2 Interface - 16 operations (5 queries + 11 mutations)
 */
export interface InstanceRepositoryV2 {
  // Queries (5)
  findByTenantAndId(tenantId: string, instanceId: string): Promise<GqlInstance | null>;
  listByStatus(tenantId: string, status: InstanceStatus, limit?: number): Promise<GqlInstance[]>;
  listByWorkflow(tenantId: string, workflowDefinitionId: string, limit?: number): Promise<GqlInstance[]>;
  findByCorrelationId(tenantId: string, correlationId: string): Promise<GqlInstance | null>;
  listActive(tenantId: string, limit?: number): Promise<GqlInstance[]>;

  // Mutations (11)
  create(tenantId: string, input: GqlCreateInstanceInput): Promise<GqlInstance>;
  start(tenantId: string, instanceId: string): Promise<GqlInstance>;
  pause(tenantId: string, instanceId: string): Promise<GqlInstance>;
  resume(tenantId: string, instanceId: string): Promise<GqlInstance>;
  complete(tenantId: string, instanceId: string, outputData: unknown): Promise<GqlInstance>;
  fail(tenantId: string, instanceId: string, errorMessage: string, errorCode?: string): Promise<GqlInstance>;
  cancel(tenantId: string, instanceId: string): Promise<GqlInstance>;
  updateProgress(tenantId: string, instanceId: string, completedTasks: number, totalTasks: number): Promise<GqlInstance>;
  update(tenantId: string, instanceId: string, input: GqlUpdateInstanceInput): Promise<GqlInstance>;
  recordEvent(tenantId: string, instanceId: string, eventId: string, eventType: string): Promise<GqlInstance>;
}

// =============================================================================
// Factory Function
// =============================================================================

/**
 * Create an Instance Repository V2 instance
 *
 * @param client Apollo GraphQL client
 * @returns InstanceRepositoryV2 implementation
 */
export function createInstanceRepositoryV2(
  client: ApolloClient
): InstanceRepositoryV2 {
  return {
    // ========================================
    // Queries (5)
    // ========================================

    async findByTenantAndId(tenantId: string, instanceId: string): Promise<GqlInstance | null> {
      const { data } = await client.query<Record<string, any>>({
        query: GET_INSTANCE,
        variables: { tenantId, instanceId },
        fetchPolicy: 'network-only',
      });
      return data?.getInstance || null;
    },

    async listByStatus(tenantId: string, status: InstanceStatus, limit: number = 50): Promise<GqlInstance[]> {
      const { data } = await client.query<Record<string, any>>({
        query: LIST_INSTANCES_BY_STATUS,
        variables: { tenantId, status, limit },
        fetchPolicy: 'network-only',
      });
      return data?.listInstancesByStatus?.items || [];
    },

    async listByWorkflow(
      tenantId: string,
      workflowDefinitionId: string,
      limit: number = 50
    ): Promise<GqlInstance[]> {
      const { data } = await client.query<Record<string, any>>({
        query: LIST_INSTANCES_BY_WORKFLOW,
        variables: { tenantId, workflowDefinitionId, limit },
        fetchPolicy: 'network-only',
      });
      return data?.listInstancesByWorkflow?.items || [];
    },

    async findByCorrelationId(tenantId: string, correlationId: string): Promise<GqlInstance | null> {
      const { data } = await client.query<Record<string, any>>({
        query: GET_INSTANCE_BY_CORRELATION,
        variables: { tenantId, correlationId },
        fetchPolicy: 'network-only',
      });
      return data?.getInstanceByCorrelation || null;
    },

    async listActive(tenantId: string, limit: number = 50): Promise<GqlInstance[]> {
      const { data } = await client.query<Record<string, any>>({
        query: LIST_ACTIVE_INSTANCES,
        variables: { tenantId, limit },
        fetchPolicy: 'network-only',
      });
      return data?.listActiveInstances?.items || [];
    },

    // ========================================
    // Mutations (11)
    // ========================================

    async create(tenantId: string, input: GqlCreateInstanceInput): Promise<GqlInstance> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: CREATE_INSTANCE,
        variables: { tenantId, input },
      });
      return data?.createInstance ?? null;
    },

    async start(tenantId: string, instanceId: string): Promise<GqlInstance> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: START_INSTANCE,
        variables: { tenantId, instanceId },
      });
      return data?.startInstance ?? null;
    },

    async pause(tenantId: string, instanceId: string): Promise<GqlInstance> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: PAUSE_INSTANCE,
        variables: { tenantId, instanceId },
      });
      return data?.pauseInstance ?? null;
    },

    async resume(tenantId: string, instanceId: string): Promise<GqlInstance> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: RESUME_INSTANCE,
        variables: { tenantId, instanceId },
      });
      return data?.resumeInstance ?? null;
    },

    async complete(tenantId: string, instanceId: string, outputData: unknown): Promise<GqlInstance> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: COMPLETE_INSTANCE,
        variables: { tenantId, instanceId, outputData },
      });
      return data?.completeInstance ?? null;
    },

    async fail(
      tenantId: string,
      instanceId: string,
      errorMessage: string,
      errorCode?: string
    ): Promise<GqlInstance> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: FAIL_INSTANCE,
        variables: {
          tenantId,
          instanceId,
          input: { errorMessage, errorCode },
        },
      });
      return data?.failInstance ?? null;
    },

    async cancel(tenantId: string, instanceId: string): Promise<GqlInstance> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: CANCEL_INSTANCE,
        variables: { tenantId, instanceId },
      });
      return data?.cancelInstance ?? null;
    },

    async updateProgress(
      tenantId: string,
      instanceId: string,
      completedTasks: number,
      totalTasks: number
    ): Promise<GqlInstance> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: UPDATE_INSTANCE_PROGRESS,
        variables: {
          tenantId,
          instanceId,
          input: { completedTasks, totalTasks },
        },
      });
      return data?.updateInstanceProgress ?? null;
    },

    async update(tenantId: string, instanceId: string, input: GqlUpdateInstanceInput): Promise<GqlInstance> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: UPDATE_INSTANCE,
        variables: { tenantId, instanceId, input },
      });
      return data?.updateInstance ?? null;
    },

    async recordEvent(
      tenantId: string,
      instanceId: string,
      eventId: string,
      eventType: string
    ): Promise<GqlInstance> {
      const { data } = await client.mutate<Record<string, any>>({
        mutation: RECORD_INSTANCE_EVENT,
        variables: { tenantId, instanceId, eventId, eventType },
      });
      return data?.recordInstanceEvent ?? null;
    },
  };
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if instance is in terminal state
 */
export function isTerminalStatus(status: InstanceStatus): boolean {
  return [
    InstanceStatus.Completed,
    InstanceStatus.Failed,
    InstanceStatus.Cancelled,
    InstanceStatus.TimedOut,
    InstanceStatus.Compensated,
  ].includes(status);
}

/**
 * Check if instance can be paused
 */
export function canPause(instance: GqlInstance): boolean {
  return instance.status === InstanceStatus.Running;
}

/**
 * Check if instance can be resumed
 */
export function canResume(instance: GqlInstance): boolean {
  return instance.status === InstanceStatus.Paused;
}

/**
 * Calculate SLA breach risk based on deadline and progress
 */
export function calculateBreachRisk(instance: GqlInstance): 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' {
  if (!instance.deadline) return 'LOW';

  const now = new Date();
  const deadline = new Date(instance.deadline);
  const timeRemaining = deadline.getTime() - now.getTime();
  const progressPercent = instance.progressPercent || 0;

  // If already past deadline
  if (timeRemaining < 0) return 'CRITICAL';

  // Estimate time needed based on progress
  const elapsedTime = instance.durationMs || 0;
  const estimatedTotalTime = progressPercent > 0 ? elapsedTime / (progressPercent / 100) : 0;
  const estimatedRemainingTime = estimatedTotalTime - elapsedTime;

  // Risk levels based on time remaining vs estimated
  if (estimatedRemainingTime > timeRemaining) return 'CRITICAL';
  if (estimatedRemainingTime > timeRemaining * 0.8) return 'HIGH';
  if (estimatedRemainingTime > timeRemaining * 0.5) return 'MEDIUM';

  return 'LOW';
}

/**
 * Get cache hit ratio for instance
 */
export function getCacheHitRatio(instance: GqlInstance): number {
  const total = instance.cacheHits + instance.cacheMisses;
  if (total === 0) return 0;
  return instance.cacheHits / total;
}

/**
 * Check if instance has errors
 */
export function hasErrors(instance: GqlInstance): boolean {
  return instance.errorMessage !== null || instance.failedTasks > 0;
}
